import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  Person,
  Logout,
  Search as SearchIcon,
  Explore as ExploreIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ExpenseNotifications } from '@/components/Expenses';
import { BecomeHostButton } from '@/components/common';

interface NavigationProps {
  onSidebarToggle?: () => void;
  showSidebarToggle?: boolean;
  position?: 'sticky' | 'fixed';
  zIndex?: number;
}

export const Navigation: React.FC<NavigationProps> = ({
  onSidebarToggle,
  showSidebarToggle = false,
  position = 'fixed',
  zIndex,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userProfile, signOut } = useAuth();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };



  const handleLogout = async () => {
    try {
      await signOut();
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Redirect employees to admin dashboard
  React.useEffect(() => {
    if (userProfile?.userGroup === 'employees' && location.pathname === '/dashboard') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [userProfile, location.pathname, navigate]);

  // Don't show regular navigation for employees
  if (userProfile?.userGroup === 'employees') {
    return null;
  }



  // Check if user is on dashboard pages (not homepage or search pages)
  const isOnDashboardPages = () => {
    const dashboardPaths = [
      '/dashboard', 
      '/listings', 
      '/bookings', 
      '/my-bookings', 
      '/my-trips', 
      '/calendar', 
      '/inquiries', 
      '/chat', 
      '/financial', 
      '/profile', 
      '/expenses'
    ];
    return dashboardPaths.some(path => location.pathname.startsWith(path));
  };



  return (
    <>
      <AppBar
        position={position}
        sx={{
          borderRadius: 0,
          zIndex: zIndex || theme.zIndex.appBar,
        }}
      >
        <Toolbar>
          {/* Sidebar toggle — visible on mobile (xs–sm) only */}
          {showSidebarToggle && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onSidebarToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/Brand - always visible */}
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 'bold',
            }}
          >
            BnBCart
          </Typography>

          {/* Navigation buttons */}
          {isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
              {/* Dashboard button for non-sidebar layouts */}
              {!showSidebarToggle && (
                <Button
                  color="inherit"
                  component={Link}
                  to="/dashboard"
                  startIcon={<Dashboard />}
                  sx={{
                    backgroundColor: location.pathname === '/dashboard' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Dashboard
                </Button>
              )}
              
              {/* Plan a Stay button - visible on all dashboard pages */}
              {isOnDashboardPages() && (
                <Button
                  color="inherit"
                  component={Link}
                  to="/search"
                  startIcon={<ExploreIcon />}
                  sx={{
                    backgroundColor: location.pathname === '/search' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Plan a Stay
                </Button>
              )}
            </Box>
          )}

          {isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Become Host Button - only for guest-only users */}
              <BecomeHostButton size="small" />

              {/* Expense Notifications - only for hosts */}
              <ExpenseNotifications />

              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    minWidth: 200,
                    mt: 1,
                  },
                }}
              >
                {/* User Info Header */}
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userProfile?.name || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userProfile?.email}
                  </Typography>
                  {userProfile?.userGroup && (
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                      {userProfile.userGroup}
                    </Typography>
                  )}
                </Box>

                {/* Menu Items */}
                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={handleMenuClose}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Person sx={{ mr: 1.5, fontSize: 20 }} />
                  Profile Settings
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                    },
                  }}
                >
                  <Logout sx={{ mr: 1.5, fontSize: 20 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                component={Link}
                to="/auth/login"
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Login
              </Button>
              <Button
                variant="outlined"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
                component={Link}
                to="/auth/register"
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>


    </>
  );
};